/**
 * PDP gallery — mobile infinite carousel + pagination.
 *
 * The gallery is one list of images that CSS presents two ways: a two-column
 * grid from $grid-medium up, and a native scroll-snap carousel below it.
 * Scrolling, snapping and keyboard scrolling all come from the browser.
 *
 * This file adds the three things markup and CSS cannot express:
 *
 *   1. the seamless infinite loop,
 *   2. mapping live scroll position onto the pagination bars, and
 *   3. dropping the track's tab stop when it is a grid with nothing to scroll.
 *
 * INFINITE LOOP — ported from the AB Tasty variation's approach, adapted.
 * The variation emitted three identical copies of every slide into the DOM
 * because it could not author markup. Rendering three copies server-side would
 * triple the <img> tags for no benefit, so the template emits ONE list and the
 * copies are cloned here, on mobile only, and hidden by CSS on desktop (the
 * same track is the desktop grid, so leaving them visible would print every
 * product image three times).
 *
 * Layout is [clone sets, real slides, clone sets] and the carousel starts on
 * the first REAL slide, so a gesture has whole sets of headroom in either
 * direction and never reaches a physical edge. Whenever the track sits in
 * clone territory it is jumped back into the real band — visually seamless
 * because the sets are identical — with scroll-snap switched off for the write
 * so the snap engine cannot re-animate and swallow the next swipe.
 *
 * WHEN THE JUMP RUNS — three triggers, in order of importance:
 *   - ON LANDING: the scroll handler rebases the moment a slide settles onto
 *     a snap point inside clone territory (position within a couple of px of
 *     a slide boundary, delta shrunk to nothing, finger up). The slides carry
 *     `scroll-snap-stop: always`, so EVERY swipe ends in exactly this state —
 *     which makes the rebase independent of the shopper's swiping cadence.
 *     This is the industry-standard trigger for native-scroll loops: a write
 *     from an aligned position to an equally aligned position never gives the
 *     snap engine anything to animate, so snap stays ON for it.
 *   - on touchstart, as a between-gestures backstop. NOT sufficient alone —
 *     that was a real bug: rapid swiping lands every touch while the previous
 *     snap animation is still in flight, and WebKit's in-flight snap
 *     overwrites a programmatic scrollLeft written at that moment, so the
 *     rebase silently never happened and the loop jammed on the track edge.
 *   - on idle (SETTLE_MS of quiet), the original fallback, which a quick
 *     swipe cadence can starve — kept for the cases the other two cannot see.
 *
 * RUNWAY — products with few images clone MORE sets per side (see cloneSets):
 * the pathological cadence is re-flinging while the previous snap animation is
 * still in flight, which blocks every rebase trigger until a landing completes
 * unobserved-by-touch; the buffer has to absorb that whole burst.
 *
 * The indicator interpolates every bar from the live scroll position rather than
 * toggling a class at the halfway point, which is what makes the pill grow with
 * the gesture and hold wherever the finger stops. Distance is wrapped so the
 * last-to-first hand-off across the loop seam stays smooth.
 *
 * If this script never runs the carousel still scrolls and snaps — finite, with
 * the first bar keeping the active style the template rendered.
 */
(function () {
  'use strict';

  /* Used only if the custom properties in pdp/gallery.scss cannot be read. */
  var BAR_WIDTH_FALLBACK = 20;
  var BAR_WIDTH_ACTIVE_FALLBACK = 60;
  var BAR_OPACITY_MIN = 0.2;
  var BAR_OPACITY_MAX = 1;

  /* How long the scroll must be quiet before the loop reposition is allowed. */
  var SETTLE_MS = 120;

  /**
   * Delays, in ms, at which the start position is re-asserted after the first
   * successful landing. A soft PDP-to-PDP navigation can drop the PREVIOUS
   * product's scrollLeft onto this track after we have positioned it — the
   * browser's element-scroll restoration, or a late re-layout, lands it after
   * the one-shot guard has already fired and cannot be undone by it. Every
   * pass is a no-op once the track is on the first real slide, and the whole
   * ladder stops the moment the shopper touches the carousel.
   */
  var REASSERT_DELAYS = [60, 180, 360, 700, 1200];

  var raf =
    typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : function (cb) {
          return setTimeout(cb, 16);
        };

  /** Collapses a burst of calls into one, on the next frame. */
  function rafCoalesce(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      raf(function () {
        queued = false;
        fn();
      });
    };
  }

  function cssLength(element, property, fallback) {
    var value = parseFloat(
      window.getComputedStyle(element).getPropertyValue(property)
    );
    return isFinite(value) && value > 0 ? value : fallback;
  }

  function setup(root) {
    if (root.pdpGalleryBound) return;
    root.pdpGalleryBound = true;

    var track = root.querySelector('[data-pdp-gallery-track]');
    if (!track) return;

    var bars = Array.prototype.slice.call(
      root.querySelectorAll('[data-pdp-gallery-bar]')
    );

    /* Captured before any cloning, so `realSlides.length` stays the number of
       actual product images and the indicator maths keeps working. */
    var realSlides = Array.prototype.slice.call(
      track.querySelectorAll('.pdp-gallery__slide')
    );
    var slideCount = realSlides.length;

    /* Clone sets PER SIDE — enough for at least six slides of headroom each
       way. `scroll-snap-stop: always` caps every gesture at one slide, so six
       is six back-to-back swipes each launched before the previous snap
       animation finished (the only cadence that suppresses every rebase
       trigger); any landing that completes between touches re-centres the
       whole budget. The clones are byte-identical DOM whose images resolve
       from the memory cache, so extra sets cost no bytes. */
    var cloneSets = Math.max(1, Math.ceil(6 / slideCount));

    var frame = null;
    var settleTimer = null;
    var looped = false;
    var positioned = false;
    var touching = false;
    var userTouched = false;

    /* Landing detection needs the per-event delta. */
    var lastScrollLeft = 0;

    /* ------------------------------------------------------------------
       GESTURE OWNERSHIP. The stylesheet sets `touch-action: pan-y` on the
       track, so the browser can never pan it horizontally from touch — the
       vertical page scroll stays fully native (threaded, smooth, never
       preventDefault'ed for vertical gestures), and every horizontal pixel
       the track moves under a finger is written here.

       This replaces the previous write-back "pin": with native panning still
       enabled, iOS unlocks BOTH axes for a gesture that starts diagonally
       (its directional lock only engages on near-axis starts; Chrome on
       Android locks more eagerly, which is why the misfires were iOS-first),
       so the compositor panned the track sideways while the scroll handler
       yanked scrollLeft back on the main thread. One frame of displacement,
       one frame of correction, every frame — the reported jitter — and a
       thumb-arc start misread as vertical pinned an intentional swipe
       against the pan for its whole length: the reported "dragged halfway,
       snatched back". Owning the axis outright removes the fight instead of
       tuning it.

       A gesture must EARN the horizontal axis: within 30 degrees of
       horizontal over its first 10px (dx >= dy / tan(30deg)). Everything
       else — every ordinary page scroll, thumb arc included — is vertical,
       and vertical costs nothing: the track simply never moves, because
       nothing moves it. Misreading an intended swipe as vertical now fails
       soft (the image stays put; retry flatter) rather than fighting.

       Once earned, touchmove is preventDefault'ed (the listener is
       non-passive for exactly this) so the leftover vertical component
       cannot creep the page and iOS cannot reclaim the gesture mid-drag,
       the track follows the finger 1:1 with scroll-snap inlined off, and
       release settles onto a slide boundary with a short eased animation —
       nearest slide, or one over on a fling, clamped to one slide per
       gesture to match the scroll-snap-stop: always contract the native
       path still enforces for keyboard scrolling. */
    var AXIS_MIN_RATIO = 1.7320508; /* 1 / tan(30deg) */
    var AXIS_THRESHOLD_PX = 10; /* travel that locks the axis for the gesture */
    var FLING_PX_PER_MS = 0.35; /* release speed that advances a full slide */
    var SETTLE_DURATION_MS = 300;

    /* null until the first gesture crosses the classification threshold,
       then 'x' or 'y' for the rest of that touch. */
    var gestureAxis = null;
    var gestureStartX = 0;
    var gestureStartY = 0;

    /* Drag state — meaningful only while gestureAxis === 'x'. `dragBaseX` is
       the finger position AT classification, not at touchstart: measuring
       from touchstart would teleport the track by the ~10px already
       travelled the instant the drag engages. */
    var dragBaseX = 0;
    var dragStartScroll = 0;
    var dragStartSlide = 0;
    var lastMoveX = 0;
    var lastMoveT = 0;
    var fingerVelocity = 0; /* px/ms, exponentially smoothed */

    /* Settle animation state. `settleToken` supersedes an in-flight settle
       when a new touch grabs the track mid-animation — the stale frame
       callbacks see a newer token and stop writing. */
    var animating = false;
    var settleToken = 0;

    /* The one finger this carousel listens to. Multi-touch without this was
       two real bugs: a second finger's touchstart re-anchored classification
       to ITSELF while touchmove kept reading touches[0] (the first finger) —
       a cross-finger delta that classified garbage — and the first finger
       lifting mid-multi-touch started a settle the remaining finger then
       fought for 300ms. Secondary fingers are now invisible end to end. */
    var primaryTouchId = null;

    function trackedTouch(list) {
      if (primaryTouchId === null || !list) return null;
      for (var i = 0; i < list.length; i++) {
        if (list[i].identifier === primaryTouchId) return list[i];
      }
      return null;
    }

    /* ------------------------------------------------------------------
       PAGE-SCROLL ASSIST — the fix for "a diagonal swipe does nothing".

       `touch-action: pan-y` makes the BROWSER pick, on ITS initial slope
       (~45 degrees), between "vertical pan: I scroll the page natively" and
       "horizontal-dominant: forbidden, I do nothing at all and just keep
       dispatching the events". Our own gate hands the carousel only to
       gestures within 30 degrees of horizontal. Between the two boundaries —
       roughly the 30-45 degree band — the browser had declined the gesture
       and so had we: a visible dead zone where a diagonal swipe froze.

       The band's page scroll is therefore driven here. Ownership is DETECTED,
       never guessed from angles: after a gesture classifies vertical, the
       page's scrollY is compared with what it was at touchstart. If the
       browser moved it (or rubber-banded it), the gesture is native and this
       code never touches the page. Only when the page has provably stayed
       put — at least PAGE_MANUAL_MIN_PROBES vertical moves AND
       PAGE_MANUAL_MIN_MS since classification with no movement — has the
       browser declined it; then every clientY delta is fed to
       window.scrollBy and a release fling decays at the 0.998/ms rate iOS
       uses, so the band feels like the native scroll next to it. Once
       manual, a drift check (expected vs actual scrollY) demotes to native
       if any other writer shows up.

       WHY A TIME GATE, NOT JUST A MOVE COUNT. iOS commits its native pan to
       pageYOffset asynchronously, a frame or two after the finger starts
       moving. A fast, firm flick delivers its first two touchmoves BEFORE
       that commit is readable, so a move-count-only gate ("page unmoved
       after two moves") called a gesture the browser had fully taken
       "declined", went manual, and on release started a JS fling — and a
       programmatic window.scrollBy CANCELS the browser's own momentum scroll
       the instant it runs. The native coast died and the JS coast, sampled
       from a few touch deltas and quick to bail on any position mismatch,
       stopped a moment later: the page braked dead mid-flick. A native
       commit is always visible within ~35ms; a genuinely declined diagonal
       drag in the 30-45 degree band is a deliberate motion that lasts far
       longer than 100ms, so the time gate separates the two cleanly. The
       release fling is additionally armed only after two frames in which the
       page has NOT moved on its own (see armPageFling). */
    var PAGE_MODE_UNKNOWN = 0;
    var PAGE_MODE_NATIVE = 1;
    var PAGE_MODE_MANUAL = 2;
    var PAGE_DECAY_PER_MS = 0.998; /* iOS UIScrollView "normal" deceleration */
    var PAGE_FLING_MIN = 0.05; /* px/ms below which a release has no fling */
    var PAGE_MANUAL_MIN_PROBES = 3; /* vertical moves with the page unmoved... */
    var PAGE_MANUAL_MIN_MS = 100; /* ...and this long since classification, before manual */

    var pageMode = PAGE_MODE_UNKNOWN;
    var pageStartScrollY = 0;
    var pageDriven = 0; /* our accumulated manual contribution, px */
    var pageProbes = 0; /* vertical moves observed while UNKNOWN */
    var pageClassifiedAt = 0; /* Date.now() when the gesture classified 'y' */
    var lastMoveY = 0;
    var fingerVelocityY = 0; /* px/ms of page travel (finger up = positive) */
    var pageFlingToken = 0;

    function pageScrollY() {
      return window.pageYOffset || document.documentElement.scrollTop || 0;
    }

    function handleVerticalMove(touch) {
      var y = touch.clientY;
      var step = lastMoveY - y; /* finger up => positive => page scrolls down */

      var now = Date.now();
      var dt = now - lastMoveT;
      if (dt > 0) {
        var sample = step / dt;
        fingerVelocityY =
          fingerVelocityY === 0 ? sample : 0.8 * sample + 0.2 * fingerVelocityY;
        lastMoveT = now;
      }
      lastMoveY = y;

      if (pageMode === PAGE_MODE_NATIVE) return;

      if (pageMode === PAGE_MODE_UNKNOWN) {
        if (Math.abs(pageScrollY() - pageStartScrollY) > 2) {
          pageMode = PAGE_MODE_NATIVE;
          return;
        }
        pageProbes++;
        /* Grace: a just-committed native scroll needs both a few moves AND
           real time to become readable (see the design note above); until
           both have passed with the page still unmoved, assume native. */
        if (
          pageProbes < PAGE_MANUAL_MIN_PROBES ||
          Date.now() - pageClassifiedAt < PAGE_MANUAL_MIN_MS
        ) {
          return;
        }
        pageMode = PAGE_MODE_MANUAL;
        pageStartScrollY = pageScrollY();
        pageDriven = 0;
      }

      /* Another writer (a late native commit, an anchor jump) showing up
         under manual drive would mean double-scrolling — hand the page back
         and stop. At a document edge scrollBy clamps, the mismatch grows,
         and this demotes too: harmless, there is nothing left to scroll. */
      if (Math.abs(pageScrollY() - (pageStartScrollY + pageDriven)) > 24) {
        pageMode = PAGE_MODE_NATIVE;
        return;
      }

      window.scrollBy(0, step);
      pageDriven += step;
    }

    /**
     * Momentum for the manually driven band, so lifting mid-scroll coasts
     * like the native scroll either side of it instead of stopping dead.
     * Killed by any new touch anywhere (grabbing a coasting page must stop
     * it — that is what fingers do to native momentum), by reaching a
     * document edge, or by decaying out.
     */
    function startPageFling(velocity) {
      if (Math.abs(velocity) < PAGE_FLING_MIN) return;
      var token = ++pageFlingToken;
      var v = velocity;
      var lastTs = null;
      var expectedY = null; /* where OUR last write left the page */

      var kill = function () {
        pageFlingToken++;
      };
      document.addEventListener('touchstart', kill, {
        once: true,
        passive: true
      });

      var stepFrame = function (ts) {
        if (token !== pageFlingToken) return;
        var now = typeof ts === 'number' ? ts : Date.now();
        if (lastTs === null) {
          lastTs = now;
          raf(stepFrame);
          return;
        }
        var dt = Math.min(64, now - lastTs);
        lastTs = now;
        var before = pageScrollY();
        /* Someone else moved the page since our last frame (a native coast
           whose commit arrived late, an anchor jump): the browser owns it —
           stop writing at once rather than fight, since a programmatic
           scroll would kill that native motion. */
        if (expectedY !== null && Math.abs(before - expectedY) > 2) {
          document.removeEventListener('touchstart', kill);
          return;
        }
        window.scrollBy(0, v * dt);
        expectedY = pageScrollY();
        v *= Math.pow(PAGE_DECAY_PER_MS, dt);
        if (Math.abs(v) < 0.02 || expectedY === before) {
          document.removeEventListener('touchstart', kill);
          return;
        }
        raf(stepFrame);
      };
      raf(stepFrame);
    }

    /**
     * Release path for a manually driven gesture. The fling is not started
     * on the spot: for two frames the page is watched, and if it moves on
     * its own in that window the browser is coasting it natively (its
     * commit was simply late) and a JS fling would cancel that coast — so
     * nothing is started. Only a page that stays put gets the JS coast.
     * Two frames (~32ms) is below what a shopper can perceive as a pause.
     */
    function armPageFling(velocity) {
      if (Math.abs(velocity) < PAGE_FLING_MIN) return;
      var token = ++pageFlingToken;
      var restingY = pageScrollY();
      var frames = 0;
      var probe = function () {
        if (token !== pageFlingToken) return; /* a new touch landed */
        if (Math.abs(pageScrollY() - restingY) > 2) return; /* native coast */
        if (++frames < 2) {
          raf(probe);
          return;
        }
        startPageFling(velocity);
      };
      raf(probe);
    }

    /**
     * True only while the track actually overflows. Above $grid-medium the same
     * element is a grid, so there is nothing to scroll and nothing to indicate;
     * this one test covers the whole breakpoint switch without duplicating the
     * media query in JS.
     *
     * The variation had no equivalent because its carousel lived inside a
     * wrapper CSS set to `display: none` on desktop, so every measurement
     * there collapsed to 0 on its own. Ours is the desktop grid, so it always
     * measures non-zero and the overflow test is what stands in for that.
     */
    function isCarousel() {
      return track.scrollWidth - track.clientWidth > 1;
    }

    /** One slide fills the viewport, so a slide's width is the track's width. */
    function slideWidth() {
      return track.clientWidth || 0;
    }

    /** Where the first REAL slide sits: all leading clone sets in. */
    function startOffset(w) {
      return cloneSets * slideCount * w;
    }

    /**
     * A scroll container needs to be focusable to be keyboard-scrollable, but a
     * grid that does not scroll would just be an empty tab stop. The template
     * ships tabindex="0" so mobile works before this runs; here it is removed
     * once we can measure that there is nothing to scroll.
     */
    function syncFocusability() {
      if (isCarousel()) {
        track.setAttribute('tabindex', '0');
      } else {
        track.removeAttribute('tabindex');
      }
    }

    function clearBars() {
      for (var i = 0; i < bars.length; i++) {
        bars[i].style.width = '';
        bars[i].style.opacity = '';
      }
    }

    /**
     * Clone `cloneSets` sets of slides before the real ones and as many after.
     * Runs once, and only for a multi-image product — a single image has
     * nothing to loop.
     *
     * Deliberately NOT gated on isCarousel(): at DOMContentLoaded the images may
     * not have laid out yet, so scrollWidth can still equal clientWidth and the
     * track would look like a non-overflowing grid. Gating on that meant the
     * loop was never built at all. Cloning is safe at any width — the clones are
     * hidden above $grid-medium by CSS, so building them on a desktop load costs
     * nothing and means a resize down already has them.
     */
    function buildLoop() {
      if (looped || slideCount <= 1) return;
      looped = true;

      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();

      for (var s = 0; s < cloneSets; s++) {
        for (var i = 0; i < slideCount; i++) {
          before.appendChild(cloneSlide(realSlides[i]));
          after.appendChild(cloneSlide(realSlides[i]));
        }
      }

      track.insertBefore(before, realSlides[0]);
      track.appendChild(after);
      track.setAttribute('data-pdp-gallery-looped', '');
    }

    function cloneSlide(slide) {
      var clone = slide.cloneNode(true);
      clone.classList.add('pdp-gallery__slide--clone');
      /* Duplicates must not reach assistive tech or report a slide position. */
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('role');
      /* An id would be duplicated N times over. */
      clone.removeAttribute('id');
      var withId = clone.querySelectorAll('[id]');
      for (var i = 0; i < withId.length; i++) withId[i].removeAttribute('id');
      /* `data-imgid` is the theme's image-id lookup key (product.js
         `updateImages` resolves a variant's featured image with
         `[data-imgid='…']`). A clone must not be able to answer that query
         ahead of the real slide. */
      clone.removeAttribute('data-imgid');
      /* A clone is never an LCP candidate, so it must not queue at the same
         priority as the real first image. Costs nothing to drop: the URL is
         identical, so the bytes come from the memory cache either way. */
      var images = clone.querySelectorAll('img[fetchpriority]');
      for (var j = 0; j < images.length; j++) {
        images[j].removeAttribute('fetchpriority');
      }
      return clone;
    }

    /**
     * Land on the first real slide, i.e. one clone-set in. One-shot: further
     * calls return immediately, and the re-assert ladder below takes over.
     *
     * Only meaningful once the track actually scrolls: on the desktop grid
     * there is no overflow, so writing scrollLeft is a no-op and `positioned`
     * stays false — which keeps the ResizeObserver watching for the resize down
     * to mobile.
     */
    function positionStart() {
      if (positioned || !looped) return;
      var w = slideWidth();
      if (!w || !isCarousel()) return;
      positioned = true;
      track.scrollLeft = startOffset(w);
      render();
      scheduleReassert();
    }

    /**
     * Put the track back on the first real slide if something moved it, but
     * never once the shopper has touched it — an intentional swipe must not be
     * yanked back. Also re-derives the offset from the LIVE width, so a rotate
     * that changes the slide width lands on a slide boundary again instead of
     * part-way through one.
     */
    function reassert() {
      if (userTouched) return;
      var w = slideWidth();
      if (!w || !isCarousel()) return;
      if (Math.abs(track.scrollLeft - startOffset(w)) > 2) {
        track.scrollLeft = startOffset(w);
        render();
      }
    }

    function scheduleReassert() {
      for (var i = 0; i < REASSERT_DELAYS.length; i++) {
        setTimeout(reassert, REASSERT_DELAYS[i]);
      }
    }

    /**
     * Map the current position back into the real band. Identical sets make the
     * shift invisible; snap is disabled for the write so it cannot re-animate
     * and eat the next swipe. The fold is modular, not a single-set shift, so
     * a gesture that travelled deep into a multi-set buffer still lands on the
     * equivalent real position in one write.
     *
     * The restore clears the inline value rather than putting back whatever was
     * read first: the stylesheet is the only other author of scroll-snap-type
     * here, and reading it back risks capturing this function's own `none` if
     * two passes ever overlap, which would disable snapping permanently.
     *
     * NO `touching` guard here on purpose — the touchstart trigger calls this
     * while the finger is already down but before any panning, which is a safe
     * (and necessary) moment to write. The idle path adds the guard itself.
     */
    function recentre() {
      if (!looped) return;
      var w = slideWidth();
      if (!w || !isCarousel()) return;

      var setSpan = slideCount * w;
      var realStart = cloneSets * setSpan;
      var position = track.scrollLeft;
      if (position >= realStart && position < realStart + setSpan) return;

      var target = realStart + (position % setSpan);

      /* Aligned fast path: when the position sits on (or within rounding of)
         a snap point, the target is equally aligned, the snap engine has
         nothing to re-animate, and the snap-type toggle can be skipped
         entirely. Skipping it matters: restoring snap-type forces a re-snap
         evaluation, which is exactly the write-during-snap flicker the
         literature warns about, and this path runs from inside the scroll
         stream where that risk is highest. */
      var nearest = Math.round(position / w) * w;
      if (Math.abs(position - nearest) <= 2) {
        track.scrollLeft = target;
        lastScrollLeft = track.scrollLeft;
        return;
      }

      /* Unaligned (idle fallback mid-fraction): the target is off-snap, so
         snapping must be silenced for the write or the engine re-animates and
         swallows the next swipe. The restore clears the inline value rather
         than putting back a read — reading risks capturing this function's
         own 'none' if two passes overlap. */
      track.style.scrollSnapType = 'none';
      track.scrollLeft = target;
      lastScrollLeft = track.scrollLeft;
      raf(function () {
        /* Restore only with no finger down. This raf can fire mid-gesture —
           touchstart calls recentre() while a grabbed settle sits unaligned
           in clone territory — and an unconditional clear re-armed mandatory
           snap either under a drag's 1:1 writes (whole-slide quantised
           stutter) or just before classification (a native re-snap animation
           for the drag to fight). Under a finger the drag/settle paths own
           the restore: finishSettle and onTouchEnd's fallback both clear it
           at gesture end, so nothing is left stuck off. */
        if (!touching) track.style.scrollSnapType = '';
      });
    }

    /** The idle-time trigger: never reposition under an active gesture or a
        live settle animation (whose from/target were captured pre-fold; the
        starved-tab edge where the timer can outrun the frames is invisible
        anyway thanks to the modular fold, but skipping is free). */
    function seamlessLoop() {
      if (touching || animating) return;
      recentre();
    }

    function render() {
      frame = null;
      if (!bars.length) return;

      var w = slideWidth();
      if (!w || !isCarousel()) return;

      var minWidth = cssLength(bars[0], '--pdp-gallery-bar-width', BAR_WIDTH_FALLBACK);
      var maxWidth = cssLength(
        bars[0],
        '--pdp-gallery-bar-width-active',
        BAR_WIDTH_ACTIVE_FALLBACK
      );

      /* Read live: clientWidth changes on rotate and across the breakpoint.
         Math.abs because a right-to-left locale can report scrollLeft negative.
         The modulo folds every cloned set back onto real-slide space. */
      var position = Math.abs(track.scrollLeft) / w;
      if (looped) position = position % slideCount;

      for (var i = 0; i < bars.length; i++) {
        var distance = Math.abs(i - position);
        /* Shortest way round, so the last-to-first hand-off over the loop seam
           grows the first bar instead of stretching the last one. */
        if (looped) distance = Math.min(distance, slideCount - distance);
        /* 1 on the bar, easing to 0 a full slide away. */
        var nearness = 1 - Math.min(distance, 1);

        bars[i].style.width =
          (minWidth + (maxWidth - minWidth) * nearness).toFixed(1) + 'px';
        bars[i].style.opacity = (
          BAR_OPACITY_MIN +
          (BAR_OPACITY_MAX - BAR_OPACITY_MIN) * nearness
        ).toFixed(3);
        bars[i].classList.toggle('pdp-gallery__bar--active', nearness > 0.5);
      }
    }

    function schedule() {
      if (frame !== null) return;
      frame = raf(render);
    }

    function scheduleLoop() {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(seamlessLoop, SETTLE_MS);
    }

    function onResize() {
      syncFocusability();
      if (isCarousel()) {
        buildLoop();
        /* First run on a desktop-to-mobile resize; a no-op after that. */
        positionStart();
        /* Re-centre after a rotate, but never fight a shopper mid-browse. */
        reassert();
        schedule();
      } else {
        /* Crossing into the grid would otherwise freeze the inline width and
           opacity mid-gesture and carry them back on the next resize down. */
        clearBars();
      }
    }

    /**
     * Ease the track onto a slide boundary. Runs with snap inlined off (set
     * at drag engagement) and re-enables it only at the final, aligned
     * position, where re-enabling gives the snap engine nothing to animate —
     * the same contract recentre()'s aligned fast path relies on.
     */
    function settleTo(targetLeft) {
      var token = ++settleToken;
      var from = track.scrollLeft;
      if (Math.abs(targetLeft - from) < 1) {
        finishSettle(targetLeft);
        return;
      }
      animating = true;
      var startedAt = null;
      var step = function (ts) {
        if (token !== settleToken) return; /* superseded by a new touch */
        var now = typeof ts === 'number' ? ts : Date.now();
        if (startedAt === null) startedAt = now;
        var progress = Math.min(1, (now - startedAt) / SETTLE_DURATION_MS);
        var eased = 1 - Math.pow(1 - progress, 3);
        track.scrollLeft = from + (targetLeft - from) * eased;
        if (progress < 1) {
          raf(step);
          return;
        }
        finishSettle(targetLeft);
      };
      raf(step);
    }

    function finishSettle(targetLeft) {
      animating = false;
      track.scrollLeft = targetLeft;
      /* Clear, never read back — recentre() documents why. */
      track.style.scrollSnapType = '';
      recentre();
      scheduleLoop();
    }

    function onTouchEnd(event) {
      /* Only the tracked finger ends the gesture. A missing changedTouches
         (or an emptied touches list on a cancel) always ends it, so an
         engine quirk can never leave `touching` latched true. */
      var endedPrimary =
        !event ||
        trackedTouch(event.changedTouches) !== null ||
        !event.touches ||
        event.touches.length === 0;
      if (!endedPrimary) return;
      primaryTouchId = null;

      touching = false;
      var axis = gestureAxis;
      gestureAxis = null;

      if (axis === 'y' && pageMode === PAGE_MODE_MANUAL) {
        if (Date.now() - lastMoveT > 100) fingerVelocityY = 0;
        armPageFling(fingerVelocityY);
      }
      pageMode = PAGE_MODE_UNKNOWN;

      if (axis === 'x') {
        /* A drag that HOLDS still and then lifts sends no touchmove while
           holding, so the last velocity sample can be hundreds of ms old —
           stale enough to fake a fling the shopper visibly abandoned. */
        if (Date.now() - lastMoveT > 100) fingerVelocity = 0;
        var w = slideWidth();
        if (w) {
          /* Nearest slide by position; a fling that would round back to the
             starting slide advances one instead. scrollLeft runs opposite to
             the finger, hence the sign flip. Clamped to one slide per
             gesture no matter how far the drag travelled — the same contract
             scroll-snap-stop: always gives the native paths. */
          var scrollVelocity = -fingerVelocity;
          var target = Math.round(track.scrollLeft / w);
          if (target === dragStartSlide && Math.abs(scrollVelocity) > FLING_PX_PER_MS) {
            target = dragStartSlide + (scrollVelocity > 0 ? 1 : -1);
          }
          target = Math.max(dragStartSlide - 1, Math.min(dragStartSlide + 1, target));
          settleTo(target * w);
          return;
        }
      }

      /* Not a drag — but if a previous settle was grabbed and abandoned
         mid-flight (snap still inlined off, position mid-slide), it must
         still be finished, or the track parks between slides with snapping
         disabled. */
      if (track.style.scrollSnapType === 'none' && !animating) {
        var width = slideWidth();
        if (width) {
          settleTo(Math.round(track.scrollLeft / width) * width);
          return;
        }
        track.style.scrollSnapType = '';
      }
      scheduleLoop();
    }

    /* Unconditional: even a one-image gallery has to drop its tab stop when it
       becomes a non-scrolling grid, and get it back on a resize down. */
    syncFocusability();
    window.addEventListener('resize', onResize);

    /* Everything below exists only to loop and to indicate, and one image has
       nothing to loop and no bars to drive. Returning here also keeps the
       ResizeObserver — which only ever disconnects once the carousel has been
       positioned — from being installed on a track that can never position. */
    if (slideCount <= 1) return;

    track.addEventListener(
      'scroll',
      function () {
        var position = track.scrollLeft;
        var delta = position - lastScrollLeft;
        lastScrollLeft = position;

        /* Vertical-intent pin. iOS pans BOTH axes on a diagonal gesture, so a
           finger heading up the page also crept the carousel sideways. Once
           the gesture is classified vertical, every horizontal step the
           browser takes is written straight back — the page keeps its native
           vertical scroll (never prevented, listener is passive) while the
           track holds still, which is UIScrollView's directional lock rebuilt
           for the web. The anchor is a snap point, so the write-back is an
           aligned write that cannot trigger a re-snap. */
        schedule();
        scheduleLoop();

        /* THE LANDING REBASE — the trigger that actually holds up under a
           fast swipe cadence. `scroll-snap-stop: always` means every gesture
           ends by settling onto a snap point; the settle is visible in the
           stream as "aligned position, near-zero delta, finger up". Rebasing
           right there, via recentre()'s aligned fast path, restores the full
           runway after every single swipe no matter how quickly the next one
           follows. `touching` is excluded because a drag also passes through
           aligned positions, and a write under a tracking finger is either
           discarded or fought by the pan gesture. */
        if (!touching && !animating && looped) {
          var w = slideWidth();
          if (w && Math.abs(delta) <= 12) {
            var setSpan = slideCount * w;
            var realStart = cloneSets * setSpan;
            if (position < realStart || position >= realStart + setSpan) {
              var nearest = Math.round(position / w) * w;
              if (Math.abs(position - nearest) <= 2) recentre();
            }
          }
        }
      },
      { passive: true }
    );
    track.addEventListener(
      'touchstart',
      function (event) {
        /* Secondary fingers are invisible — see primaryTouchId. */
        if (touching) return;
        var touch = event.changedTouches && event.changedTouches[0];
        if (!touch) return;
        primaryTouchId = touch.identifier;

        touching = true;
        userTouched = true;
        /* A new touch takes the track over from wherever it is: an in-flight
           settle is superseded (its frames see a stale token), and the drag,
           if this gesture earns one, starts from the grabbed position. */
        settleToken++;
        animating = false;
        /* Between-gestures backstop. With horizontal motion script-owned
           there is no native snap animation left to eat this write on the
           touch path; keyboard-initiated native scrolls remain, which is why
           the landing rebase above stays. */
        recentre();
        /* Gesture classification anchors — AFTER recentre, so a drag that
           engages measures from the post-jump position. */
        gestureAxis = null;
        gestureStartX = touch.clientX;
        gestureStartY = touch.clientY;
        lastMoveX = gestureStartX;
        lastMoveY = gestureStartY;
        lastMoveT = Date.now();
        fingerVelocity = 0;
        fingerVelocityY = 0;
        pageMode = PAGE_MODE_UNKNOWN;
        pageStartScrollY = pageScrollY();
        pageDriven = 0;
        pageProbes = 0;
        pageFlingToken++;
      },
      { passive: true }
    );
    /* NON-passive on purpose: once a gesture has earned the horizontal
       axis, preventDefault() stops the browser spending the gesture's
       vertical remainder on a page scroll (the creep) and stops iOS
       reclaiming the touch mid-drag. Vertical and unclassified gestures
       never reach preventDefault, so page scrolling stays native and the
       listener costs those gestures nothing. */
    track.addEventListener(
      'touchmove',
      function (event) {
        var touch = trackedTouch(event.touches);
        if (!touch) return;

        if (gestureAxis === null) {
          var dx = Math.abs(touch.clientX - gestureStartX);
          var dy = Math.abs(touch.clientY - gestureStartY);
          /* First 10px decide the axis for the whole gesture — the same
             activation-threshold pattern native gesture recognisers use.
             Deliberately asymmetric: 'x' has to be EARNED (within 30 degrees
             of horizontal), everything else is 'y'. A plain `dy > dx` split
             put the boundary at 45 degrees and handed an exact 45-degree
             diagonal to the carousel. */
          if (Math.max(dx, dy) < AXIS_THRESHOLD_PX) return;
          /* The desktop grid does not scroll; a drag there would only eat
             the page's own pan. */
          gestureAxis =
            dx >= dy * AXIS_MIN_RATIO && isCarousel() ? 'x' : 'y';
          if (gestureAxis === 'y') pageClassifiedAt = Date.now();
          if (gestureAxis === 'x') {
            /* Engage: snap off for the duration of finger-driven writes
               (a mandatory snap re-snaps every programmatic scrollLeft
               assignment), measure from here, not from touchstart. */
            track.style.scrollSnapType = 'none';
            dragBaseX = touch.clientX;
            dragStartScroll = track.scrollLeft;
            var w = slideWidth();
            dragStartSlide = w ? Math.round(dragStartScroll / w) : 0;
            lastMoveX = touch.clientX;
            lastMoveT = Date.now();
          }
        }

        if (gestureAxis === 'y') {
          /* No preventDefault on this path, ever: when the browser owns the
             scroll there is nothing to prevent without breaking it, and when
             it has declined the gesture there is no default left. */
          handleVerticalMove(touch);
          return;
        }
        if (gestureAxis !== 'x') return;

        if (event.cancelable) event.preventDefault();

        /* Finger velocity for the release decision. The smoothing favours
           the NEWEST sample (0.8/0.2) so the release reflects how the drag
           actually ended; the fling threshold and the one-slide clamp are
           what bound a single wild sample, not the smoothing. */
        var now = Date.now();
        var dt = now - lastMoveT;
        if (dt > 0) {
          var sample = (touch.clientX - lastMoveX) / dt;
          fingerVelocity =
            fingerVelocity === 0
              ? sample
              : 0.8 * sample + 0.2 * fingerVelocity;
          lastMoveX = touch.clientX;
          lastMoveT = now;
        }

        /* 1:1 with the finger; the scroll event this write fires drives the
           pagination bars exactly as a native pan did. */
        track.scrollLeft = dragStartScroll - (touch.clientX - dragBaseX);
      },
      { passive: false }
    );
    track.addEventListener('touchend', onTouchEnd, { passive: true });
    /* Without this a cancelled gesture — the finger sliding into browser
       chrome, or a second finger landing — would leave `touching` true for
       good, and the loop would never reposition again. */
    track.addEventListener('touchcancel', onTouchEnd, { passive: true });

    buildLoop();
    /* Synchronously first: on a mobile load the track is already laid out here,
       which avoids a frame of "last image, then jump to the first". The rAF
       covers the case where it is not. */
    positionStart();
    raf(positionStart);
    schedule();

    /* The track can gain width after this runs — a font swap, a late image, or a
       desktop-to-mobile resize — and `positionStart` is a no-op until it can
       measure. Keep watching until the carousel is actually positioned, then
       stop. Disconnecting merely because a width existed used to leave an
       unpositioned (and therefore non-looping) carousel behind. */
    if (typeof window.ResizeObserver === 'function') {
      var observer = new window.ResizeObserver(
        rafCoalesce(function () {
          buildLoop();
          positionStart();
          if (positioned) observer.disconnect();
        })
      );
      observer.observe(track);
    }

    /* Images finishing after DOMContentLoaded change the track's metrics; a
       single settle pass covers the common case without polling. */
    window.addEventListener('load', function () {
      buildLoop();
      positionStart();
      reassert();
      schedule();
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-pdp-gallery]');
    for (var i = 0; i < roots.length; i++) {
      setup(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
